// All bullet lists are extracted to module-scope constants so React doesn't
// re-allocate them on every render (a real perf win on these long legal pages).

const DEFINITIONS_LIST = [
    "\u201CPlatform\u201D refers to Connexa website and services.",
    "\u201CUser\u201D refers to anyone accessing or using the platform.",
    "\u201CQR Owner\u201D refers to a registered user generating QR codes.",
    "\u201CScanner\u201D refers to a person interacting with a QR code.",
];

const SERVICES_LIST = [
    "QR code generation",
    "Lead capture forms",
    "WhatsApp connection workflows",
    "Analytics dashboards",
    "Subscription-based business tools",
];

const ACCOUNT_RESPONSIBILITIES_LIST = [
    "Providing accurate information",
    "Maintaining account confidentiality",
    "Restricting unauthorized access",
    "All activities under their account",
];

const PROHIBITED_USE_LIST = [
    "Use the platform for spam or unsolicited messaging",
    "Violate privacy laws",
    "Misrepresent identity",
    "Send harmful, abusive, or illegal content",
    "Attempt unauthorized access to systems",
    "Use the service for fraudulent activities",
];

const THIRD_PARTY_DISCLAIMERS_LIST = [
    "WhatsApp service interruptions",
    "Message delivery failures",
    "Account restrictions imposed by third-party services",
];

const IP_RESTRICTIONS_LIST = ["Copy", "Resell", "Reverse engineer", "Modify", "Distribute platform materials without permission"];

const LEAD_RESPONSIBILITIES_LIST = [
    "How they use collected lead information",
    "Compliance with privacy laws",
    "Obtaining required permissions and consents",
];

const LIABILITY_EXCLUSIONS_LIST = [
    "Indirect damages",
    "Loss of profits",
    "Business interruption",
    "Data loss",
    "Lead loss",
    "Third-party service failures",
];

const INDEMNIFICATION_LIST = [
    "Misuse of the platform",
    "Violation of laws",
    "Violation of these Terms",
    "Improper communication activities",
];

export default function Terms() {
    return (
        <section className="min-h-screen pt-40 pb-16 mesh-gradient" data-testid="terms-page">
            <div className="max-w-3xl mx-auto px-6">
                <div className="mb-10">
                    <span className="inline-block px-4 py-2 rounded-full glass-light text-xs font-semibold text-brand-400 mb-4">LEGAL</span>
                    <h1 className="font-display font-bold text-4xl mb-3">Terms &amp; Conditions</h1>
                    <p className="text-gray-500 text-sm">Effective Date: May 28, 2026</p>
                </div>

                <div className="glass rounded-3xl p-8 md:p-10 space-y-8 text-gray-300 text-[15px] leading-relaxed">
                    <p>
                        These Terms and Conditions (&ldquo;Terms&rdquo;) govern the use of the <strong>Connexa</strong> website, platform, services, QR tools, and related applications. By accessing or using our services, you agree to these Terms.
                    </p>

                    <Section title="1. Definitions">
                        <List items={DEFINITIONS_LIST} />
                    </Section>

                    <Section title="2. Eligibility">
                        <p>Users must be at least 18 years old and legally capable of entering into binding agreements.</p>
                    </Section>

                    <Section title="3. Services">
                        <p>Connexa provides:</p>
                        <List items={SERVICES_LIST} />
                        <p className="mt-3">We reserve the right to modify or discontinue services at any time.</p>
                    </Section>

                    <Section title="4. Account Registration">
                        <p>Users are responsible for:</p>
                        <List items={ACCOUNT_RESPONSIBILITIES_LIST} />
                        <p className="mt-3">We reserve the right to suspend or terminate accounts suspected of misuse.</p>
                    </Section>

                    <Section title="5. Acceptable Use">
                        <p>Users agree NOT to:</p>
                        <List items={PROHIBITED_USE_LIST} />
                        <p className="mt-3">Violation may result in account termination.</p>
                    </Section>

                    <Section title="6. WhatsApp and Third-Party Platforms">
                        <p>Connexa uses external platforms such as WhatsApp. We are not responsible for:</p>
                        <List items={THIRD_PARTY_DISCLAIMERS_LIST} />
                        <p className="mt-3">Users must comply with all applicable third-party platform rules and policies.</p>
                    </Section>

                    <Section title="7. Subscription and Billing">
                        <h4 className="font-semibold text-white mt-3 mb-1">A. Paid Plans</h4>
                        <p>Certain features require paid subscriptions.</p>
                        <h4 className="font-semibold text-white mt-3 mb-1">B. Billing</h4>
                        <p>Subscriptions may renew automatically unless canceled.</p>
                        <h4 className="font-semibold text-white mt-3 mb-1">C. Refunds</h4>
                        <p>Unless otherwise specified, payments are non-refundable. Refund requests may be reviewed at our discretion.</p>
                        <h4 className="font-semibold text-white mt-3 mb-1">D. Pricing Changes</h4>
                        <p>We reserve the right to revise pricing and subscription plans.</p>
                    </Section>

                    <Section title="8. Intellectual Property">
                        <p>All platform content, branding, software, logos, and designs are the intellectual property of Connexa unless otherwise stated. Users may not:</p>
                        <List items={IP_RESTRICTIONS_LIST} />
                    </Section>

                    <Section title="9. User Content">
                        <p>
                            Users retain ownership of submitted content. However, by using the platform, users grant Connexa a limited license to process and display content necessary for service functionality. Users are solely responsible for their uploaded or transmitted content.
                        </p>
                    </Section>

                    <Section title="10. Data and Lead Responsibility">
                        <p>QR Owners are solely responsible for:</p>
                        <List items={LEAD_RESPONSIBILITIES_LIST} />
                        <p className="mt-3">Connexa is not responsible for misuse of collected lead data.</p>
                    </Section>

                    <Section title="11. Platform Availability">
                        <p>We do not guarantee uninterrupted or error-free service. Maintenance, outages, or technical issues may temporarily affect availability.</p>
                    </Section>

                    <Section title="12. Limitation of Liability">
                        <p>To the maximum extent permitted by law, Connexa shall not be liable for:</p>
                        <List items={LIABILITY_EXCLUSIONS_LIST} />
                    </Section>

                    <Section title="13. Indemnification">
                        <p>Users agree to indemnify and hold harmless Connexa from claims, liabilities, damages, or expenses arising from:</p>
                        <List items={INDEMNIFICATION_LIST} />
                    </Section>

                    <Section title="14. Termination">
                        <p>We reserve the right to suspend or terminate access to the platform at any time for violations of these Terms.</p>
                    </Section>

                    <Section title="15. Governing Law">
                        <p>These Terms shall be governed by the laws of India. Any disputes shall be subject to the jurisdiction of courts located in Mumbai, Maharashtra.</p>
                    </Section>

                    <Section title="16. Changes to Terms">
                        <p>We may update these Terms periodically. Continued use of the platform after updates constitutes acceptance of revised Terms.</p>
                    </Section>

                    <Section title="17. Contact Information">
                        <p>For support or legal inquiries:</p>
                        <div className="bg-white/5 rounded-xl p-4 mt-3 text-sm">
                            <div><strong className="text-white">Connexa Support</strong></div>
                            <div className="text-gray-400">Email: <a href="mailto:Support@connexa.co.in" className="text-brand-400 hover:underline">Support@connexa.co.in</a></div>
                            <div className="text-gray-400">Phone: <a href="tel:+919136581868" className="text-brand-400 hover:underline">+91 91365 81868</a></div>
                            <div className="text-gray-400">Website: <a href="https://www.connexa.in" target="_blank" rel="noreferrer" className="text-brand-400 hover:underline">www.connexa.in</a></div>
                        </div>
                    </Section>
                </div>
            </div>
        </section>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <h2 className="font-display font-bold text-xl text-white mb-3">{title}</h2>
            <div className="space-y-2">{children}</div>
        </div>
    );
}

function List({ items }) {
    return (
        <ul className="list-disc list-inside space-y-1 ml-2 text-gray-300">
            {items.map((it) => <li key={it}>{it}</li>)}
        </ul>
    );
}
